import type { GitPersistenceSettings, SpecifiedGitRef, ProjectResource, GitRef, PersistenceSettings } from "@octopusdeploy/octopus-server-client";
import { HasGitPersistenceSettings, toGitBranch } from "@octopusdeploy/octopus-server-client";
import { getValueInStorage, removeValueInStorage, setValueInStorage } from "~/hooks/localStorageHelpers";

interface StoredGitRef {
    GitRef: SpecifiedGitRef;
    Url: string;
    BasePath: string;
}

function isStoredGitRef(value: object): value is StoredGitRef {
    return "GitRef" in value && typeof value.GitRef === "string" && "Url" in value && typeof value.Url === "string" && "BasePath" in value && typeof value.BasePath === "string";
}

function parseJson(value: string): unknown {
    try {
        return JSON.parse(value);
    } catch {
        return undefined;
    }
}

// Repositories can be referenced with or without a trailing slash or ".git" suffix, and hosts are
// case-insensitive. Normalise before comparing so cosmetic differences don't discard a valid, still-relevant entry.
function normalizeRepositoryUrl(url: string): string {
    return url
        .trim()
        .replace(/\.git\/?$/i, "")
        .replace(/\/$/, "")
        .toLowerCase();
}

function settingsMatch(stored: StoredGitRef, settings: GitPersistenceSettings): boolean {
    return normalizeRepositoryUrl(stored.Url) === normalizeRepositoryUrl(settings.Url) && stored.BasePath === settings.BasePath;
}

function lastAccessedGitRefStore() {
    const LocalStorageKey = "octoLastAccessedGitRef";

    const buildKey = (projectIdOrSlug: string) => `${LocalStorageKey}-${projectIdOrSlug}`;

    const save = async (project: ProjectResource, gitRef: SpecifiedGitRef): Promise<void> => {
        if (!HasGitPersistenceSettings(project.PersistenceSettings)) {
            throw new Error("Can store GitRef for Git Projects");
        }

        const stored: StoredGitRef = { GitRef: gitRef, Url: project.PersistenceSettings.Url, BasePath: project.PersistenceSettings.BasePath };
        setValueInStorage(buildKey(project.Id), JSON.stringify(stored));
    };

    const clear = (projectId: string): void => {
        removeValueInStorage(buildKey(projectId));
    };

    // Reads whatever is stored for this project, discarding (and clearing) it if it was saved against
    // a different Git repository than the one the project currently points at. This can happen when the
    // project's version control settings are changed by something other than the portal, e.g. the Terraform
    // provider or the API - see https://github.com/OctopusDeploy/Issues/issues/10190.
    const readStoredGitRef = (key: string, settings: GitPersistenceSettings): GitRef | undefined => {
        const rawValue = getValueInStorage(key);
        if (!rawValue) {
            return undefined;
        }

        const parsed = parseJson(rawValue);

        if (typeof parsed !== "object" || parsed === null || !isStoredGitRef(parsed)) {
            // Value predates this format - a bare GitRef string (or, if the stored value is corrupt, garbage) with
            // no repository fingerprint to check. Treat it as-is; it will be replaced with a fingerprinted entry
            // on the next save.
            return rawValue;
        }

        if (settingsMatch(parsed, settings)) {
            return parsed.GitRef;
        }

        // The remembered branch belongs to a repository this project no longer points at. Discard it
        // rather than asking the server to resolve it against the current repository.
        removeValueInStorage(key);
        return undefined;
    };

    const get = (project: ProjectResource): GitRef => {
        if (!HasGitPersistenceSettings(project.PersistenceSettings)) {
            throw new Error("Can only retrieve stored GitRef for Git Projects");
        }

        return readStoredGitRef(buildKey(project.Id), project.PersistenceSettings) ?? toGitBranch(project.PersistenceSettings.DefaultBranch);
    };

    const getById = (projectId: string, persistenceSettings: PersistenceSettings): GitRef => {
        if (!HasGitPersistenceSettings(persistenceSettings)) {
            throw new Error("Can only retrieve stored GitRef for Git Projects");
        }

        return readStoredGitRef(buildKey(projectId), persistenceSettings) ?? toGitBranch(persistenceSettings.DefaultBranch);
    };

    return {
        save,
        get,
        getById,
        clear,
    };
}

const lastAccessedGitRef = lastAccessedGitRefStore();

export { lastAccessedGitRef };
